
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  ArrowUpRight,
  GraduationCap,
  BookOpen,
  Globe2,
  Users,
  Sparkles,
  Building2,
  Award,
  ExternalLink,
  Quote,
  Landmark,
  BriefcaseBusiness,
} from "lucide-react";

import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function Deans({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Deans | Bandarban University",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // =========================================
  // ANIMATION VARIANTS
  // =========================================

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 35,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const staggerContainer = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 35,
      scale: 0.97,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.65,
        ease: "easeOut",
      },
    },
  };

  // =========================================
  // DEAN INFORMATION
  // =========================================

  const deans = [
    {
      id: 1,
      name: "Professor Dr. M. Nurul Islam",
      designation:
        "Dean, School of Arts, Social Science and Law",
      image: "/assets/img/nurul-islam-bu.jpeg",
      school: "School of Arts, Social Science and Law",
      number: "01",
    },
    {
      id: 2,
      name: "Prof. Dr. Kazi Ahmed Nabi",
      designation:
        "Dean, School of Business Administration",
      image: "/assets/img/kazi-ahmed.jpeg",
      school: "School of Business Administration",
      number: "02",
    },
  ];

  // =========================================
  // ACADEMIC PROFILE LINKS
  // =========================================

  const researchLinks = [
    {
      name: "Google Scholar",
      color: "#4285F4",
      icon: BookOpen,
      url: "#",
    },
    {
      name: "ResearchGate",
      color: "#00CCBB",
      icon: GraduationCap,
      url: "#",
    },
    {
      name: "ORCID",
      color: "#A6CE39",
      icon: Globe2,
      url: "#",
    },
    {
      name: "Scopus",
      color: "#E87524",
      icon: Award,
      url: "#",
    },
    {
      name: "Web of Science",
      color: "#6D4BC3",
      icon: ExternalLink,
      url: "#",
    },
  ];

  // =========================================
  // VISION / MISSION / GOALS
  // =========================================

  const objectives = [
    {
      number: "01",
      title: "Our Vision",
      subtitle: "A Global Academic Presence",
      description:
        "To become a leading institution of higher learning in Asia.",
      icon: Globe2,
    },
    {
      number: "02",
      title: "Our Mission",
      subtitle: "Education & Leadership",
      description:
        "To nurture creativity, research, and leadership through quality education.",
      icon: GraduationCap,
    },
    {
      number: "03",
      title: "Our Goals",
      subtitle: "Innovation & Collaboration",
      description:
        "Expanding research opportunities and strengthening global partnerships.",
      icon: Users,
    },
  ];

  return (
    <div
      className="min-h-screen bg-[#f7faf8] text-gray-800 overflow-hidden"
      style={{
        "--accent": accent,
      }}
    >
      {/* =========================================
          HERO SECTION
      ========================================= */}

      <section
        className="relative isolate overflow-hidden"
        style={{
          background: `linear-gradient(
            135deg,
            ${accent} 0%,
            #0b4027 48%,
            #062b1b 100%
          )`,
        }}
      >
        {/* Animated Background */}

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 40,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute -top-48 -right-40 w-[600px] h-[600px] rounded-full border border-white/10"
          />

          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 30, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-48 -left-40 w-[600px] h-[600px] rounded-full bg-emerald-300/10 blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
        </div>

        {/* Hero Content */}

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 pt-20 pb-32 md:pt-28 md:pb-40">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            {/* University Logo */}

            <motion.div
              variants={fadeUp}
              className="relative inline-flex mb-8"
            >
              <div className="absolute -inset-4 bg-white/10 rounded-full blur-xl" />

              <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full bg-white p-3 shadow-2xl ring-4 ring-white/20">
                <img
                  src={logoSrc}
                  alt="Bandarban University"
                  className="w-full h-full object-contain"
                />
              </div>
            </motion.div>

            {/* Badge */}

            <motion.div
              variants={fadeUp}
              className="flex justify-center mb-6"
            >
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-semibold backdrop-blur-md">
                <Sparkles
                  size={16}
                  className="text-yellow-300"
                />

                Academic Leadership
              </span>
            </motion.div>

            {/* Heading */}

            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight"
            >
              Deans of
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-200 via-white to-green-300">
                Schools
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-7 text-base md:text-lg text-green-50/80 leading-relaxed max-w-2xl mx-auto"
            >
              Guiding the future with vision, leadership,
              and integrity. Discover the academic leadership
              of Bandarban University.
            </motion.p>

            {/* Hero Buttons */}

            <motion.div
              variants={fadeUp}
              className="mt-9 flex flex-wrap justify-center gap-4"
            >
              <a
                href="#deans"
                className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-xl bg-white text-emerald-900 font-bold shadow-xl hover:bg-green-50 transition-all duration-300"
              >
                Meet Our Deans

                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>

              <a
                href="#contact"
                className="inline-flex items-center gap-3 px-7 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white font-semibold backdrop-blur-md hover:bg-white/20 transition-all duration-300"
              >
                Contact Office

                <ArrowUpRight size={18} />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Wave */}

        <div className="absolute bottom-0 left-0 w-full leading-none">
          <svg
            viewBox="0 0 1440 120"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-16 md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64 C240,120 480,0 720,48 C960,96 1200,24 1440,64 L1440,120 L0,120 Z"
              fill="#f7faf8"
            />
          </svg>
        </div>
      </section>

      {/* =========================================
          DEANS PROFILE SECTION
      ========================================= */}

      <section
        id="deans"
        className="relative py-16 md:py-24 px-6 sm:px-8 lg:px-16"
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Heading */}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-2 text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              <Landmark size={17} />
              Academic Leadership
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-4">
              Meet Our Deans
            </h2>

            <p className="mt-4 text-gray-500 max-w-2xl mx-auto leading-relaxed">
              Dedicated academic leadership committed to
              excellence in teaching, research, and
              institutional development.
            </p>
          </motion.div>

          {/* Dean Profile Cards */}

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            className="space-y-10"
          >
            {deans.map((dean, index) => (
              <motion.div
                key={dean.id}
                variants={cardVariants}
                className={`grid lg:grid-cols-5 bg-white rounded-[32px] overflow-hidden shadow-xl shadow-emerald-900/5 border border-gray-100 ${
                  index % 2 !== 0
                    ? "lg:[&>div:first-child]:order-2"
                    : ""
                }`}
              >
                {/* Profile Image */}

                <div className="lg:col-span-2 relative min-h-[420px] lg:min-h-[510px] overflow-hidden bg-emerald-900">
                  <motion.img
                    src={dean.image}
                    alt={dean.name}
                    initial={{
                      scale: 1.08,
                    }}
                    whileInView={{
                      scale: 1,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 1.2,
                    }}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />

                  {/* Image Overlay */}

                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/10 to-transparent" />

                  {/* Badge */}

                  <div className="absolute top-6 left-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur-md text-emerald-900 text-xs font-bold shadow-lg">
                      <Award size={15} />
                      Dean of School
                    </span>
                  </div>

                  {/* Image Footer */}

                  <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9">
                    <motion.div
                      initial={{
                        opacity: 0,
                        y: 20,
                      }}
                      whileInView={{
                        opacity: 1,
                        y: 0,
                      }}
                      viewport={{
                        once: true,
                      }}
                      transition={{
                        delay: 0.25,
                      }}
                    >
                      <div className="w-12 h-1 bg-green-400 rounded-full mb-5" />

                      <h3 className="text-2xl md:text-3xl font-extrabold text-white">
                        {dean.name}
                      </h3>

                      <p className="text-green-200 font-semibold mt-3 text-sm md:text-base">
                        {dean.designation}
                      </p>

                      <p className="text-white/70 mt-2 text-sm">
                        Bandarban University
                      </p>
                    </motion.div>
                  </div>
                </div>

                {/* Profile Details */}

                <div className="lg:col-span-3 p-7 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="text-emerald-700 text-sm font-bold uppercase tracking-widest">
                    Academic Administration
                  </span>

                  <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-4">
                    {dean.name}
                  </h2>

                  <p className="text-emerald-700 font-semibold mt-3 text-lg">
                    {dean.designation}
                  </p>

                  <p className="text-gray-500 mt-2">
                    Bandarban University
                  </p>

                  <div className="w-20 h-1 bg-emerald-500 rounded-full mt-7" />

                  {/* Quote */}

                  <div className="relative mt-7">
                    <Quote
                      size={34}
                      className="absolute -top-2 -left-1 text-emerald-100"
                    />

                    <p className="relative pl-8 text-gray-600 leading-8">
                      Guiding academic excellence through
                      quality education, research, leadership,
                      and a commitment to the advancement
                      of higher education.
                    </p>
                  </div>

                  {/* School Information */}

                  <div className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 shrink-0 rounded-xl bg-white text-emerald-700 flex items-center justify-center shadow-sm">
                        <BriefcaseBusiness size={22} />
                      </div>

                      <div>
                        <p className="text-xs text-emerald-700 font-bold uppercase tracking-wider">
                          Academic School
                        </p>

                        <p className="font-bold text-gray-800 mt-1">
                          {dean.school}
                        </p>

                        <p className="text-sm text-gray-500 mt-1">
                          Bandarban University
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Academic Links */}

                  <div className="mt-8">
                    <p className="text-sm font-bold text-gray-800 mb-4">
                      Academic & Research Profiles
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {researchLinks.map((item, linkIndex) => {
                        const Icon = item.icon;

                        return (
                          <motion.a
                            key={item.name}
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            initial={{
                              opacity: 0,
                              y: 15,
                            }}
                            whileInView={{
                              opacity: 1,
                              y: 0,
                            }}
                            viewport={{
                              once: true,
                            }}
                            transition={{
                              delay: linkIndex * 0.08,
                            }}
                            whileHover={{
                              y: -3,
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-100 bg-white text-gray-600 text-xs sm:text-sm font-semibold hover:shadow-md transition-all duration-300"
                          >
                            <Icon
                              size={15}
                              style={{
                                color: item.color,
                              }}
                            />

                            {item.name}

                            <ExternalLink
                              size={12}
                              className="text-gray-300"
                            />
                          </motion.a>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* =========================================
          VISION / MISSION / GOALS
      ========================================= */}

      <section className="relative py-20 md:py-28 px-6 sm:px-8 lg:px-16 bg-white overflow-hidden">
        {/* Background Decoration */}

        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-50 blur-3xl pointer-events-none" />

        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-green-50 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          {/* Heading */}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
            }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-2 text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              <Sparkles size={17} />
              Our Commitment
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-4">
              Vision, Mission & Goals
            </h2>

            <p className="text-gray-500 max-w-2xl mx-auto mt-4 leading-relaxed">
              Building a strong academic community through
              quality education, research, and collaboration.
            </p>
          </motion.div>

          {/* Cards */}

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.2,
            }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {objectives.map((item) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.number}
                  variants={cardVariants}
                  whileHover={{
                    y: -10,
                  }}
                  className="group relative bg-[#f8faf9] border border-gray-100 rounded-3xl p-8 overflow-hidden hover:bg-white hover:shadow-2xl hover:shadow-emerald-900/10 transition-all duration-500"
                >
                  {/* Number */}

                  <span className="absolute top-5 right-6 text-6xl font-black text-emerald-900/[0.04] group-hover:text-emerald-900/[0.08] transition">
                    {item.number}
                  </span>

                  {/* Icon */}

                  <div className="relative w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-emerald-800 group-hover:text-white group-hover:rotate-6 transition-all duration-300">
                    <Icon size={29} />
                  </div>

                  <h3 className="text-2xl font-extrabold text-gray-900 mt-7">
                    {item.title}
                  </h3>

                  <p className="text-emerald-700 font-semibold text-sm mt-2">
                    {item.subtitle}
                  </p>

                  <p className="text-gray-500 leading-7 mt-4">
                    {item.description}
                  </p>

                  {/* Bottom Accent */}

                  <div className="absolute bottom-0 left-0 w-0 h-1 bg-emerald-700 group-hover:w-full transition-all duration-500" />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* =========================================
          CONTACT SECTION
      ========================================= */}

      <section
        id="contact"
        className="relative isolate overflow-hidden"
        style={{
          background: `linear-gradient(
            135deg,
            ${accent} 0%,
            #0b4027 55%,
            #062b1b 100%
          )`,
        }}
      >
        {/* Decoration */}

        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            animate={{
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-white/[0.04] blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-16 md:py-24">
          {/* Heading */}

          <motion.div
            initial={{
              opacity: 0,
              y: 35,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.7,
            }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 border border-white/20 text-green-200 mb-6">
              <Building2 size={30} />
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-white">
              Get in Touch
            </h2>

            <p className="mt-4 text-green-50/70 max-w-2xl mx-auto leading-relaxed">
              Connect with the Office of the Deans for
              academic information, university administration,
              and official inquiries.
            </p>
          </motion.div>

          {/* Contact Information */}

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
            }}
            className="grid md:grid-cols-3 gap-5 mt-12"
          >
            {/* Address */}

            <motion.div
              variants={cardVariants}
              className="flex items-start gap-4 p-6 rounded-2xl bg-white/[0.07] border border-white/10 backdrop-blur-md hover:bg-white/[0.12] transition-all duration-300"
            >
              <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 text-green-200 flex items-center justify-center">
                <MapPin size={23} />
              </div>

              <div>
                <h3 className="text-white font-bold">
                  Office Address
                </h3>

                <p className="text-green-50/70 text-sm leading-7 mt-2">
                  K.B Road, Shualok, Post Office: Shualok -
                  4600, Bandarban Sadar, Bangladesh
                </p>
              </div>
            </motion.div>

            {/* Phone */}

            <motion.a
              variants={cardVariants}
              href="tel:+8801318305900"
              className="flex items-start gap-4 p-6 rounded-2xl bg-white/[0.07] border border-white/10 backdrop-blur-md hover:bg-white/[0.12] transition-all duration-300"
            >
              <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 text-green-200 flex items-center justify-center">
                <Phone size={23} />
              </div>

              <div>
                <h3 className="text-white font-bold">
                  Contact Number
                </h3>

                <p className="text-green-50/70 text-sm mt-2">
                  +880 1318-305900
                </p>
              </div>
            </motion.a>

            {/* Email */}

            <motion.a
              variants={cardVariants}
              href="mailto:admission@bubban.ac.bd"
              className="flex items-start gap-4 p-6 rounded-2xl bg-white/[0.07] border border-white/10 backdrop-blur-md hover:bg-white/[0.12] transition-all duration-300"
            >
              <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 text-green-200 flex items-center justify-center">
                <Mail size={23} />
              </div>

              <div>
                <h3 className="text-white font-bold">
                  Email Address
                </h3>

                <p className="text-green-50/70 text-sm mt-2 break-all">
                  admission@bubban.ac.bd
                </p>
              </div>
            </motion.a>
          </motion.div>

          {/* Appointment Button */}

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: 0.3,
            }}
            className="text-center mt-10"
          >
            <a
              href="mailto:admission@bubban.ac.bd?subject=Request%20Appointment"
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white text-emerald-900 font-bold shadow-xl hover:bg-green-50 transition-all duration-300"
            >
              Request Appointment

              <ArrowRight
                size={19}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
